import { useCallback, useEffect, useRef, useState } from 'react';
import { getAiVoiceTts } from 'shared/api';
import {
  extractTextVariableCandidates,
  replaceVariablesWithExamples,
} from 'shared/utils/merge-tags';
import { MergeTagVariable } from 'types/merge-tag-variables';
import { useUpdatingRef } from './misc';

export const useAiVoiceTts = (text: string) => {
  const [status, setStatus] = useState<
    'noText' | 'generating' | 'generated' | 'playing' | 'paused'
  >('noText');
  const [duration, setDuration] = useState<number>(0);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const textRef = useUpdatingRef(text);

  useEffect(
    function generateTtsEffect() {
      const doEffect = async () => {
        if (text) {
          if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current = null;
          }
          const candidates = extractTextVariableCandidates(text);
          const mergeVars: MergeTagVariable[] = candidates.map(c => ({
            key: c.key,
            exampleValue:
              c.key === 'contact.name'
                ? 'Alice Smith'
                : c.fallback || `(${c.key} Variable not supported)`,
            fallbackValue: c.fallback,
            description: '',
          }));
          const textMerged = replaceVariablesWithExamples(text, mergeVars, true);
          setStatus('generating');
          const res = await getAiVoiceTts(textMerged);
          if (textRef.current !== text) {
            // text has changed since we started the request, cancel the rest
            // of the effect, a new one will be started with the new text
            return;
          }
          const blob = new Blob([res.data], { type: 'audio/mpeg' });
          const url = URL.createObjectURL(blob);
          audioRef.current = new Audio(url);
          audioRef.current.addEventListener('ended', () => {
            setStatus('paused');
            setCurrentTime(0);
          });
          audioRef.current.addEventListener('loadedmetadata', () => {
            setDuration(audioRef.current?.duration || 0);
          });
          audioRef.current.addEventListener('timeupdate', () => {
            setCurrentTime(audioRef.current?.currentTime || 0);
          });
          setStatus('generated');
        }
      };
      doEffect();
    },
    [text, textRef],
  );

  useEffect(function unmountCleanupEffect() {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const play = useCallback(async () => {
    if (audioRef.current && audioRef.current.paused) {
      await audioRef.current.play();
      setStatus('playing');
    }
  }, []);

  const pause = useCallback(() => {
    if (audioRef.current && !audioRef.current.paused) {
      audioRef.current.pause();
      setStatus('paused');
      return;
    }
  }, []);

  return {
    status,
    text,
    audioRef,
    pause,
    play,
    duration,
    currentTime,
  };
};

export const useLazyAiVoiceTts = (text: string) => {
  const [status, setStatus] = useState<
    'noText' | 'generating' | 'generated' | 'playing' | 'paused'
  >('noText');
  const [duration, setDuration] = useState<number>(0);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const textRef = useUpdatingRef(text);

  function generateTtsEffect() {
    const doEffect = async () => {
      if (text) {
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current = null;
        }
        const candidates = extractTextVariableCandidates(text);
        const mergeVars: MergeTagVariable[] = candidates.map(c => ({
          key: c.key,
          exampleValue:
            c.key === 'contact.name'
              ? 'Alice Smith'
              : c.fallback || `(${c.key} Variable not supported)`,
          fallbackValue: c.fallback,
          description: '',
        }));
        const textMerged = replaceVariablesWithExamples(text, mergeVars, true);
        setStatus('generating');
        const res = await getAiVoiceTts(textMerged);
        if (textRef.current !== text) {
          // text has changed since we started the request, cancel the rest
          // of the effect, a new one will be started with the new text
          return;
        }
        const blob = new Blob([res.data], { type: 'audio/mpeg' });
        const url = URL.createObjectURL(blob);
        audioRef.current = new Audio(url);
        audioRef.current.addEventListener('ended', () => {
          setStatus('paused');
          setCurrentTime(0);
        });
        audioRef.current.addEventListener('loadedmetadata', () => {
          setDuration(audioRef.current?.duration || 0);
        });
        audioRef.current.addEventListener('timeupdate', () => {
          setCurrentTime(audioRef.current?.currentTime || 0);
        });
        setStatus('generated');
      }
    };
    return doEffect();
  }

  useEffect(function unmountCleanupEffect() {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const play = async () => {
    if (status === 'noText') {
      generateTtsEffect().then(async () => {
        if (audioRef.current && audioRef.current.paused) {
          await audioRef.current.play();
          setStatus('playing');
        }
      });

      return;
    }

    if (audioRef.current && audioRef.current.paused) {
      await audioRef.current.play();
      setStatus('playing');
    }
  };

  const pause = useCallback(() => {
    if (audioRef.current && !audioRef.current.paused) {
      audioRef.current.pause();
      setStatus('paused');
      return;
    }
  }, []);

  return {
    status,
    text,
    audioRef,
    pause,
    play,
    duration,
    currentTime,
  };
};
